import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { faBell, faBellSlash } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ArchiveIcon,
  ExternalLinkIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import { differenceInHours, differenceInMinutes } from "date-fns";
import { ContactListRead, InboxContactRead } from "@hilos/types/private-schema";
import CopyPhoneToClipboardButton from "src/components/Form/CopyPhoneToClipboardButton";
import { classNames, contactToString, formatPhone, formatWhatsAppUsername } from "../../../Helpers";
import { getDistanceFromNow } from "../../../helpers/date";
import { buildRoute } from "../../../router/router";
import ContactNameWithStatus from "../ContactNameWithStatus";
import { getConnectionColor } from "../Helpers";

interface ConversationDetailsHeaderProps {
  inboxContact: InboxContactRead;
  contact: ContactListRead;
  silenced: boolean;
  archived: boolean;
  lastInboundMessageOn: string | null;
  onSwitchSilenced?: () => void;
  onSwitchArchived?: () => void;
}

function ConversationDetailsHeader({
  inboxContact,
  contact,
  silenced,
  archived,
  lastInboundMessageOn,
  onSwitchSilenced,
  onSwitchArchived,
}: ConversationDetailsHeaderProps) {
  const { t } = useTranslation();

  const statusColor = useMemo(
    () => getConnectionColor(lastInboundMessageOn),
    [lastInboundMessageOn]
  );

  const remainingTime = useMemo(() => {
    if (!lastInboundMessageOn) return null;

    const lastMessage = new Date(lastInboundMessageOn);
    const closeDate = new Date(lastMessage.getTime() + 24 * 60 * 60 * 1000);
    const now = new Date();

    if (closeDate <= now) return null;

    const hours = differenceInHours(closeDate, now);
    const minutes = differenceInMinutes(closeDate, now) % 60;

    return { hours, minutes };
  }, [lastInboundMessageOn]);

  return (
    <div className="flex-rows mr-2 items-center text-center justify-center font-medium text-gray-800">
      {archived && (
        <span className="ml-1 mb-1 text-xs text-gray-400">(Archived)</span>
      )}
      {!contact.first_name && !contact.last_name ? (
        <div className="flex justify-center min-w-0 max-w-full">
          <ContactNameWithStatus
            channel={inboxContact.channel}
            name={contactToString(contact)}
            lastInboundMessageOn={lastInboundMessageOn}
            className="ml-2"
          />
        </div>
      ) : (
        <>
          <div className="flex justify-center min-w-0 max-w-full">
            <ContactNameWithStatus
              channel={inboxContact.channel}
              name={contactToString(contact)}
              lastInboundMessageOn={lastInboundMessageOn}
              className="ml-2"
            />
          </div>
          {contact.phone ? (
            <div className="text-sm text-gray-500 ml-6">
              <CopyPhoneToClipboardButton
                value={contact.canonical_phone as string}
                text={formatPhone(contact.phone, true, true)}
              />
            </div>
          ) : (
            contact.whatsapp_username && (
              <div className="text-sm text-gray-500 ml-6">
                {formatWhatsAppUsername(contact.whatsapp_username)}
              </div>
            )
          )}
        </>
      )}

      {lastInboundMessageOn && remainingTime && (
        <div className="text-xs text-gray-400">
          {t("inbox:time-remaining", "{{hours}}h {{minutes}}min remaining", {
            hours: remainingTime.hours,
            minutes: remainingTime.minutes,
          })}
        </div>
      )}

      {lastInboundMessageOn && statusColor !== "green" && (
        <div className="text-xs text-gray-400">
          ({getDistanceFromNow(lastInboundMessageOn)})
        </div>
      )}

      {!!contact.external_url && (
        <a
          className="flex items-center justify-center text-sm text-blue-500"
          href={contact.external_url}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLinkIcon className="mr-1 h-4 w-4" />
          <span>{t("see-in-crm", "See in CRM")}</span>
        </a>
      )}

      <Link
        className="inline items-center justify-center text-sm text-blue-500"
        to={buildRoute("contact-update", {
          id: contact.id,
        })}
        target="_blank"
        rel="noreferrer"
      >
        <button
          type="button"
          className="hidden h-8 w-8 items-center justify-center rounded-full text-gray-500 transition duration-150 ease-in-out hover:bg-gray-300 focus:outline-none sm:inline-flex"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
      </Link>

      {onSwitchSilenced && (
        <button
          type="button"
          className="hidden h-8 w-8 items-center justify-center rounded-full text-gray-500 transition duration-150 ease-in-out hover:bg-gray-300 focus:outline-none sm:inline-flex"
          onClick={onSwitchSilenced}
        >
          <FontAwesomeIcon
            icon={silenced ? faBellSlash : faBell}
            className="h-5 w-5"
          />
        </button>
      )}
      {onSwitchArchived && (
        <button
          type="button"
          className="hidden h-8 w-8 items-center justify-center rounded-full text-gray-500 transition duration-150 ease-in-out hover:bg-gray-300 focus:outline-none sm:inline-flex"
          onClick={onSwitchArchived}
        >
          <ArchiveIcon
            className={classNames("h-5 w-5", archived && "text-gray-800")}
          />
        </button>
      )}
    </div>
  );
}

export default ConversationDetailsHeader;
